/**
 * Reusable success banner component
 * Consolidates duplicated banner display logic from detail.tsx and edit.tsx
 */

import React from "react";
import { Banner } from "@dreambigger/design-system/src/components/";
import { COLORS, SUCCESS_BANNER_STYLE, INFO_BANNER_STYLE } from "../../util/constants";

export interface SuccessBannerProps {
  text: string;
  type?: "success" | "info";
  closable?: boolean;
  ctaText?: string;
  ctaFunction?: () => void;
  className?: string;
  facebookShareEnabled?: boolean;
}

/**
 * Pre-styled success/info banner component
 */
export const SuccessBanner: React.FC<SuccessBannerProps> = ({
  text,
  type = "success",
  closable = false,
  ctaText,
  ctaFunction,
  className,
  facebookShareEnabled = false,
}) => {
  const isSuccess = type === "success";

  return (
    <Banner
      icon={isSuccess ? "checkmark" : "earth-americas"}
      className={className}
      text1={text}
      textColor={isSuccess ? "white" : undefined}
      darkHoverText={true}
      iconBackground={isSuccess ? COLORS.SUCCESS_GREEN_DARK : undefined}
      uiColor={isSuccess ? "white" : COLORS.INFO_BLUE}
      closable={closable}
      ctaText={ctaText}
      ctaLinkOrFunction={ctaFunction}
      facebookShareEnabled={facebookShareEnabled}
      style={isSuccess ? SUCCESS_BANNER_STYLE : INFO_BANNER_STYLE}
    />
  );
};

export interface RsvpSuccessBannerProps {
  totalRecipients?: string | string[];
  onSendAnother: () => void;
  className?: string;
}

/**
 * RSVP success banner with recipient count and "Send Another" CTA
 */
export const RsvpSuccessBanner: React.FC<RsvpSuccessBannerProps> = ({
  totalRecipients,
  onSendAnother,
  className,
}) => {
  const text = totalRecipients
    ? `Your invitation has been sent to ${totalRecipients} ${
        totalRecipients === "1" ? "recipient" : "recipients"
      }!`
    : `Your invitation has been sent!`;

  return (
    <SuccessBanner
      text={text}
      type="success"
      closable={true}
      ctaText="Send Another"
      ctaFunction={onSendAnother}
      className={className}
    />
  );
};

export interface AdminWelcomeBannerProps {
  firstName?: string;
  className?: string;
}

/**
 * Admin welcome banner shown to event owners
 */
export const AdminWelcomeBanner: React.FC<AdminWelcomeBannerProps> = ({
  firstName,
  className,
}) => {
  return (
    <Banner
      icon="earth-americas"
      className={className}
      text1={`Welcome to your project${firstName ? `, ${firstName}!` : `!`}`}
      uiColor={COLORS.INFO_BLUE}
      text2="This is how friends and family will view your page."
      closable={true}
      facebookShareEnabled={true}
      style={INFO_BANNER_STYLE}
    />
  );
};

export default SuccessBanner;
